import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import { mapErrors } from "../../Utils";
import { ResourceLoading } from "../../utils/ResourceLoading";
import PMListboxFormik from "../../utils/PMListboxFormik";
import { PMField } from "../../utils/PMField";
import { useSelector } from "react-redux";
import { QuillEditor } from "../../utils/quill-editor";
import { fetchContact } from "../../../api/customer/contact";
import {
  sendOffer,
  getPdfOffer,
  checkSendOfferStatus,
} from "../../../api/offer";

export const OfferSendDialog = (props) => {
  const { onClose, open, isLoading, offer } = props;
  const { t } = useTranslation();

  const [contact, setContact] = useState(null);
  const [isLoadingContact, setIsLoadingContact] = useState(false);
  const [offerType, setOfferType] = useState("offer");
  const [pdfUrl, setPdfUrl] = useState("");
  const [jobStatus, setJobStatus] = useState(null);

  const initialValues = {
    id: offer?.id,
    sendOffer: true,
    printOfferType: "offer",
    email: "",
    name: "",
    message: "",
    submit: null,
  };

  const currentUser = useSelector((state) => state.session.currentUser);

  const handleSend = async (payload) => {
    const response = await sendOffer(payload);

    if (response?.job_id) {
      setJobStatus("queued");
      pollJobStatus(response.job_id);
    }

    return response;
  };

  const pollJobStatus = async (jobId) => {
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setJobStatus("timeout");
        toast.error(
          t("Request timed out. Please check the offer status manually.")
        );
        return;
      }

      try {
        const statusResponse = await checkSendOfferStatus(jobId);
        const status = statusResponse?.job_status;

        setJobStatus(status);

        if (status === "completed") {
          props.onRefresh?.(statusResponse);
          toast.success(t("Offer sent successfully!"));
          return;
        } else if (status === "failed") {
          const errorMessage =
            statusResponse?.metadata?.error ||
            t("Failed to send offer. Please try again.");
          toast.error(errorMessage);
          return;
        } else if (status === "processing" || status === "queued") {
          attempts++;
          setTimeout(poll, 5000);
        }
      } catch (error) {
        console.error("Error checking job status:", error);
        attempts++;
        setTimeout(poll, 5000);
      }
    };

    poll();
  };

  const handleReceiverChange = (receiver, formik) => {
    formik.setFieldValue("email", receiver?.email);
    formik.setFieldValue("name", receiver?.name);
    if (receiver?.printOfferType) {
      formik.setFieldValue("printOfferType", receiver.printOfferType);
      setOfferType(receiver.printOfferType);
      fetchPdfOffer(receiver.printOfferType);
    }
  };

  const fetchPdfOffer = async (offerType) => {
    if (!offer?.id) return false;

    const payload = {
      id: offer?.id,
      params: {
        printOfferType: offerType,
      },
    };
    try {
      const response = await getPdfOffer(payload);
      setPdfUrl(response?.pdf_url || "");
      return response;
    } catch (err) {
      console.log(err);
      return null;
    }
  };

  const handleOfferTypeChange = (data) => {
    setOfferType(data?.value);
    if (data?.value) {
      fetchPdfOffer(data.value);
    }
  };

  useEffect(() => {
    const loadContact = async () => {
      if (open && offer?.contact?.id) {
        setIsLoadingContact(true);
        try {
          const response = await fetchContact(offer.contact.id);
          if (response?.data) {
            setContact(response.data);
          }
        } catch (error) {
          console.error("Error fetching contact:", error);
          toast.error(t("Failed to load contact information"));
        } finally {
          setIsLoadingContact(false);
        }
      } else {
        setContact(null);
      }
    };

    loadContact();
  }, [open, offer?.contact?.id, t]);

  useEffect(() => {
    if (open && offerType) {
      fetchPdfOffer(offerType);
    }
  }, [open, offerType]);

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full sm:max-w-2xl max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="mt-3 sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      {t("Send Offer")}
                    </Dialog.Title>
                    <p className="text-gray-500 text-sm mt-2">
                      {t("Choose recipient, offer PDF version and customer message before sending.")}
                    </p>

                    {isLoading ? (
                      <ResourceLoading />
                    ) : !offer?.id ? (
                      "Missing ID"
                    ) : (
                      <Formik
                        enableReinitialize
                        initialValues={initialValues}
                        onSubmit={async (values, helpers) => {
                          try {
                            helpers.setSubmitting(true);

                            const payload = {
                              id: values.id,
                              sendOffer: values.sendOffer,
                              printOfferType: values.printOfferType,
                              email: values.email,
                              name: values.name,
                              message: values.message,
                            };

                            const response = await handleSend(payload);

                            if (response?.error || response?.errors?.length) {
                              throw new Error(
                                response?.error ||
                                  mapErrors(response.errors) ||
                                  "Failed to send offer"
                              );
                            }

                            if (response?.job_id) {
                              toast.success(
                                t(
                                  "Offer sending has been queued. You will be notified when it's complete."
                                )
                              );
                            } else {
                              toast.success(t("Offer sent successfully!"));
                              props.onRefresh?.(response);
                              onClose();
                            }
                            helpers.setStatus({ success: true });
                          } catch (err) {
                            toast.error(
                              t(
                                "Something went wrong. Could not send offer."
                              )
                            );
                            console.error(err);
                            helpers.setStatus({ success: false });
                            helpers.setErrors({ submit: err.message });
                          } finally {
                            helpers.setSubmitting(false);
                          }
                        }}
                      >
                        {(formik) => (
                          <Form>
                            <div className="grid grid-cols-6 gap-6 mt-10">
                              <div className="col-span-6 sm:col-span-6">
                                <div className="flex flex-col">
                                  <span className="text-gray-400 text-sm mb-2">
                                    {t("Send to")}{" "}
                                  </span>
                                  <span className="isolate inline-flex rounded-md shadow-sm">
                                    <button
                                      onClick={() =>
                                        handleReceiverChange(
                                          {
                                            email: currentUser?.email,
                                            name: currentUser?.name,
                                            printOfferType: "offer",
                                          },
                                          formik
                                        )
                                      }
                                      type="button"
                                      className="relative inline-flex items-center rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                                    >
                                      {t("Me")}
                                    </button>
                                    <button
                                      onClick={
                                        offer?.customer?.email
                                          ? () =>
                                              handleReceiverChange(
                                                {
                                                  email:
                                                    offer?.customer?.email,
                                                  name: offer?.customer?.name,
                                                  printOfferType:
                                                    "customer_offer",
                                                },
                                                formik
                                              )
                                          : () =>
                                              toast.error(
                                                "Customer Email is missing"
                                              )
                                      }
                                      type="button"
                                      className="relative -ml-px inline-flex items-center bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                                    >
                                      {t("Customer")}
                                    </button>
                                    <button
                                      onClick={
                                        contact?.email
                                          ? () =>
                                              handleReceiverChange(
                                                {
                                                  email: contact?.email,
                                                  name:
                                                    contact?.name ||
                                                    contact?.displayTitle,
                                                  printOfferType:
                                                    "customer_offer",
                                                },
                                                formik
                                              )
                                          : () =>
                                              toast.error(
                                                t("Contact Email is missing")
                                              )
                                      }
                                      type="button"
                                      disabled={
                                        isLoadingContact || !contact?.email
                                      }
                                      className="relative -ml-px inline-flex items-center rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {isLoadingContact
                                        ? t("Loading...")
                                        : t("Contact")}
                                    </button>
                                  </span>
                                </div>
                              </div>
                              <div className="col-span-6 sm:col-span-6">
                                <PMField
                                  name="name"
                                  label={t("Name")}
                                  {...formik}
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-6">
                                <PMField
                                  name="email"
                                  label={t("Email")}
                                  {...formik}
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-6">
                                <PMListboxFormik
                                  label={t("Offer Type")}
                                  name="printOfferType"
                                  onChange={handleOfferTypeChange}
                                  options={[
                                    {
                                      name: "printOfferType",
                                      value: "offer",
                                      label: t("Internal offer"),
                                    },
                                    {
                                      name: "printOfferType",
                                      value: "customer_offer",
                                      label: t("Customer offer"),
                                    },
                                  ]}
                                  {...formik}
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-6">
                                {pdfUrl ? (
                                  <a
                                    href={pdfUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-sm font-medium text-blue-600 hover:text-blue-800"
                                  >
                                    {t("Open selected offer PDF")}
                                  </a>
                                ) : (
                                  <p className="text-sm text-gray-500">
                                    {t("PDF preview will be available when the selected offer type is generated.")}
                                  </p>
                                )}
                              </div>
                              <div className="col-span-6 sm:col-span-6 mb-[200px]">
                                <span className="text-sm px-1">
                                  {t("Message to customer")}
                                </span>
                                <div className="mt-1">
                                  <QuillEditor
                                    name="message"
                                    rows={4}
                                    {...formik}
                                  />
                                </div>
                              </div>

                              {/* Job Status Indicator */}
                              {jobStatus && (
                                <div className="col-span-6 sm:col-span-6">
                                  <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="flex items-center">
                                      <div className="flex-shrink-0">
                                        {jobStatus === "queued" && (
                                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                        )}
                                        {jobStatus === "processing" && (
                                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600"></div>
                                        )}
                                        {jobStatus === "completed" && (
                                          <div className="rounded-full h-5 w-5 bg-green-600 flex items-center justify-center">
                                            <svg
                                              className="h-3 w-3 text-white"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          </div>
                                        )}
                                        {jobStatus === "failed" && (
                                          <div className="rounded-full h-5 w-5 bg-red-600 flex items-center justify-center">
                                            <svg
                                              className="h-3 w-3 text-white"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          </div>
                                        )}
                                      </div>
                                      <div className="ml-3">
                                        <p className="text-sm font-medium text-gray-900">
                                          {jobStatus === "queued" &&
                                            t("Offer sending queued...")}
                                          {jobStatus === "processing" &&
                                            t(
                                              "Generating PDF and sending offer..."
                                            )}
                                          {jobStatus === "completed" &&
                                            t("Offer sent successfully!")}
                                          {jobStatus === "failed" &&
                                            t("Failed to send offer")}
                                          {jobStatus === "timeout" &&
                                            t("Request timed out")}
                                        </p>
                                        {jobStatus === "queued" ||
                                        jobStatus === "processing" ? (
                                          <p className="text-sm text-gray-500">
                                            {t(
                                              "This may take a few minutes. You can close this dialog."
                                            )}
                                          </p>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="mt-20 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                              <button
                                type="button"
                                className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                                onClick={onClose}
                                disabled={
                                  jobStatus === "queued" ||
                                  jobStatus === "processing"
                                }
                              >
                                {t("Close")}{" "}
                              </button>

                              <button
                                type="button"
                                className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={formik.submitForm}
                                disabled={
                                  jobStatus === "queued" ||
                                  jobStatus === "processing"
                                }
                              >
                                {jobStatus === "queued" ||
                                jobStatus === "processing"
                                  ? t("Sending...")
                                  : t("Send")}
                              </button>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};


